/**
 * Shared indicator library — define once, reuse across the chart (full series,
 * configurable params), the scanner (last value at fixed presets OR user-chosen
 * params via the on-demand endpoint), and the watchlist grid. See ./types.ts
 * for the contract.
 */
export type {
  OHLCV,
  SharedParam,
  SharedFormat,
  IndicatorParams,
  SharedIndicatorDef,
} from "./types";
export { resolveParams } from "./types";
export { emaSeries, lastFinite } from "./ema";
export { FORCE_MOMENTUM } from "./forceMomentum";
export { RELATIVE_VOLUME } from "./relativeVolume";
export { VOLUME } from "./volume";
export { TECHNICAL_INDICATORS } from "./technicals";
export { MPP_INDICATORS } from "./mpp";

import { FORCE_MOMENTUM } from "./forceMomentum";
import { RELATIVE_VOLUME } from "./relativeVolume";
import { VOLUME } from "./volume";
import { TECHNICAL_INDICATORS } from "./technicals";
import { MPP_INDICATORS } from "./mpp";
// Client-safe metadata (no lightweight-charts-indicators import). The heavy
// library + the actual math live in the SERVER-ONLY libraryColumns.compute.ts,
// injected by computeIndicator.ts for lib:* ids — see that file for the split.
import { LIBRARY_SCALAR_INDICATORS, LIBRARY_COLUMN_PREFIX } from "./libraryColumns.generated";
import type { SharedIndicatorDef } from "./types";

export { LIBRARY_SCALAR_INDICATORS, LIBRARY_COLUMN_PREFIX } from "./libraryColumns.generated";
export { studyShortName, chromeStudyLabel } from "../studyShortName";

/** The CURATED shared indicators, in display order: Force/Volume first, then the
 *  configurable scalar technicals (momentum / trend / volatility / volume),
 *  then the centralized MPP Elite set (Squeeze, Dots, Bongo, RS, …). These are
 *  the ones the scanner WORKER precomputes into `scanner_panel` and the chart
 *  reuses — the big library catalog is deliberately NOT in here. */
export const SHARED_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [
  FORCE_MOMENTUM,
  RELATIVE_VOLUME,
  VOLUME,
  ...TECHNICAL_INDICATORS,
  ...MPP_INDICATORS,
];

/** Everything the scanner's "+ Indicator" column picker can add: the curated
 *  set PLUS the full library catalog (`lib:*`). Resolution (the compute route,
 *  `makeDynamicCol`, the live overlay) goes through `SHARED_INDICATOR_BY_ID`,
 *  which is keyed over BOTH — so a `lib:*` id resolves exactly like a curated
 *  one. The worker never iterates this list, so the library defs stay
 *  on-demand-only and don't touch the precompute path. */
export const PICKER_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [
  ...SHARED_INDICATORS,
  ...LIBRARY_SCALAR_INDICATORS,
].map((d) =>
  // Tag every Pivot Studies indicator with the "Afiat - " prefix; skip the
  // ones already named with it so they aren't double-prefixed.
  d.group === "Pivot Studies" && !d.label.startsWith("Afiat")
    ? { ...d, label: `Afiat - ${d.label}` }
    : d,
);

export const SHARED_INDICATOR_BY_ID = new Map<string, SharedIndicatorDef>(
  PICKER_INDICATORS.map((d) => [d.id, d]),
);
